import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { uploadConnectorService, UploadProgress, UploadResult } from '../services/uploadConnector';

interface ActiveUpload {
  id: string;
  progress: UploadProgress;
  filesCount: number;
  batchName?: string;
  startTime: Date;
}

interface UploadContextType {
  activeUpload: ActiveUpload | null;
  isUploading: boolean;
  startUpload: (
    storeId: string,
    files: File[],
    batchName?: string
  ) => Promise<UploadResult>;
  skipOverlay: () => void;
  showOverlay: boolean;
  lastResult: UploadResult | null;
  error: string | null;
}

const UploadContext = createContext<UploadContextType | null>(null);

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const [activeUpload, setActiveUpload] = useState<ActiveUpload | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [lastResult, setLastResult] = useState<UploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const uploadIdRef = useRef(0);

  const startUpload = useCallback(async (
    storeId: string,
    files: File[],
    batchName?: string
  ): Promise<UploadResult> => {
    const uploadId = `upload-${++uploadIdRef.current}`;

    setError(null);
    setLastResult(null);
    setShowOverlay(true);

    // Set initial state
    setActiveUpload({
      id: uploadId,
      progress: {
        stage: 'preparing',
        percentage: 0,
        filesTotal: files.length,
      },
      filesCount: files.length,
      batchName,
      startTime: new Date(),
    });

    try {
      const result = await uploadConnectorService.uploadImages(
        storeId,
        files,
        batchName,
        (progress) => {
          setActiveUpload(prev => prev?.id === uploadId ? {
            ...prev,
            progress,
          } : prev);
        }
      );

      // Show complete state
      setActiveUpload(prev => prev?.id === uploadId ? {
        ...prev,
        progress: {
          stage: 'complete',
          percentage: 100,
          filesTotal: files.length,
          filesProcessed: result.stats.success,
        },
      } : prev);

      setLastResult(result);

      // Keep complete state visible for 3 seconds, then clear
      setTimeout(() => {
        setActiveUpload(prev => prev?.id === uploadId ? null : prev);
        setShowOverlay(false);
      }, 3000);

      return result;
    } catch (err: any) {
      setError(err.message || 'Upload failed');

      setActiveUpload(prev => prev?.id === uploadId ? {
        ...prev,
        progress: {
          stage: 'error',
          percentage: 0,
          message: err.message || 'Upload failed',
        },
      } : prev);

      // Keep error visible for 5 seconds
      setTimeout(() => {
        setActiveUpload(prev => prev?.id === uploadId ? null : prev);
        setShowOverlay(false);
      }, 5000);

      throw err;
    }
  }, []);

  const skipOverlay = useCallback(() => {
    setShowOverlay(false);
  }, []);

  const isUploading = activeUpload !== null &&
    !['complete', 'error'].includes(activeUpload.progress.stage);

  return (
    <UploadContext.Provider value={{
      activeUpload,
      isUploading,
      startUpload,
      skipOverlay,
      showOverlay,
      lastResult,
      error,
    }}>
      {children}
    </UploadContext.Provider>
  );
}

export function useUpload() {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
}
